import React, { useState } from 'react'
import { Linking, Platform } from 'react-native'
import { NativeStackScreenProps } from '@react-navigation/native-stack'
import { shallowEqual } from 'react-redux'
import styled, { useTheme } from 'styled-components/native'

import Config from 'APP/Config'
import type { AccountScreenLink } from 'APP/Config/types'
import { useDispatch, useSelector } from 'APP/Hooks/redux'
import I18n from 'APP/I18n'

// Actions
import LoginActions from 'APP/Redux/LoginRedux'
import StartupActions from 'APP/Redux/StartupRedux'

// Components
import PharmacyAccountMenuItem from 'APP/Components/AccountScreen/PharmacyAccountMenuItem'
import List from 'APP/Components/List'
import NoticeBarContainer from 'APP/Components/NoticeBarContainer'
import ProfileHeaderCard from 'APP/Components/ProfileHeaderCard'
import { QRCodeAppModal } from 'APP/Components/QRCodeAppModal'
import Typography from 'APP/Converse/Typography'
import ActivityIndicator from 'APP/Components/ActivityIndicator'
import ListItem from 'APP/Converse/ListItem'
import Icon from 'APP/Converse/Icon'
import Button from 'APP/Converse/Button'
import { NotificationIconBadge } from 'APP/Components/NotificationIconBadge'
import VirtualCareAccountBanner from 'APP/Components/VirtualCareAccountBanner'

// Libs
import { useServiceFeatures } from 'APP/Hooks/ServiceFeatures'

// Styling
import { isMobile, isWeb } from 'APP/Helpers/checkPlatform'
import { ResponsiveScrollView } from 'APP/Converse/Layout'
import { logger } from 'APP/Lib/Datadog'
import { isEmbeddedEnvironment } from 'APP/BrownfieldBridge/utils'
import { selectIsAppOnline } from 'APP/Sagas/AppSessionSagas'

const ButtonContainer = styled.View`
  max-width: 800px;
  width: 100%;
  margin-top: ${({ theme }) => theme.metrics.baseMargin}px;
  margin-horizontal: auto;
  padding-horizontal: ${({ theme }) => theme.metrics.baseMargin * 2}px;
  // Without the extra padding on web, it sits flush to the screen, but mobile handles insets better
  padding-bottom: ${({ theme }) =>
    isWeb() ? theme.insets.bottom + theme.metrics.baseMargin : theme.insets.bottom}px;
`

const QRCodeTextContainer = styled.View`
  display: flex;
  gap: ${({ theme }) => theme.metrics.baseMargin / 4}px;
  flex-direction: row;
`

const StyledActivityIndicatorContainer = styled.View`
  flex: 1;
  justify-content: center;
  align-items: center;
`

const AccountScreenLinkMenuItems = ({
  accountScreenLinks = [],
}: {
  accountScreenLinks?: AccountScreenLink[]
}) => {
  const goToLink = (url: string) => {
    Linking.openURL(url).catch((e) => {
      logger.error('[AccountLinkingScreen] failed to open url', { url }, e)
    })
  }

  return accountScreenLinks.map((linkFromConfig, index) => {
    const localizedLink = linkFromConfig[I18n.baseLocale] || {}

    const { url, title } = localizedLink
    if (!url || !title) {
      return null
    }

    return (
      <ListItem
        key={`account-screen-link${index}`}
        analyticsName={`account-screen-link${index}`}
        testID={`ACCOUNT_SCREEN_LINK_MENU_ITEM${index}`}
        title={title}
        onPress={() => goToLink(url)}
        iconName="exit-to-app"
      />
    )
  })
}

export type AccountScreenProps = NativeStackScreenProps<ReactNavigation.RootParamList, 'account'>

const AccountScreen = ({ navigation, route }: AccountScreenProps) => {
  const theme = useTheme()
  const { isLoggingOut } = useSelector((state) => state.login)
  const [isQRCodeModalOpen, setQRCodeModalOpen] = useState(false)
  const {
    email,
    givenName,
    familyName,
    pronouns,
    creditCard,
    preferredName,
    isConnected,
    showFamily,
    showDebugToggle,
    hasEligibilityIntervals,
    showNotifications,
    showSecurity,
  } = useSelector((state) => {
    const profile = state.patient.profile
    const family = state.family
    const hasFamilyAccess = profile?.hasFamilyAccess
    const hasFamily = Object.keys(family.members).length > 1
    const enableDebugMenu = __DEV__ || Config.ENABLE_DEBUG_MENU
    const customClaims = state.login.customClaims || {}
    const ssoConnection = customClaims['sso-connection']
    const disabledOnSso =
      typeof ssoConnection === 'string' &&
      Boolean(Config.ACCOUNT_SECURITY_DISABLED_ON_SSO_CONNECTION?.includes(ssoConnection))
    return {
      email: profile?.email,
      givenName: profile?.givenName,
      familyName: profile?.familyName,
      pronouns: profile?.pronouns,
      creditCard: profile?.creditCard,
      preferredName: profile?.preferredName,
      isConnected: selectIsAppOnline(state),
      showFamily: (hasFamily || hasFamilyAccess) && !Config.HIDE_FAMILY_TAB,
      showDebugToggle: enableDebugMenu ? !state.app.showDebugFab : false,
      hasEligibilityIntervals: profile?.eligibilityIntervals?.length > 0,
      showNotifications:
        !!state.notifications.preferences?.data &&
        Object.keys(state.notifications.preferences?.data).length > 0,
      showSecurity: !disabledOnSso,
    }
  }, shallowEqual)
  const unreadCount = useSelector((state) => state.zendesk.unreadCount)
  const serviceFeatures = useServiceFeatures()
  const dispatch = useDispatch()
  const toggleDebugFab = () => dispatch(StartupActions.toggleDebugFab())
  const logout = (reason: string) => dispatch(LoginActions.logout(reason, true))
  const rateApp = (action: string) => dispatch(StartupActions.rateApp(action))

  const goToAppStore = () => {
    const store = Platform.OS === 'ios' ? Config.APP_STORE : Config.PLAY_STORE
    const url = Platform.OS === 'ios' ? Config.APP_STORE_URL : Config.PLAY_STORE_URL
    if (!store) return

    const localizedUrl = url?.[I18n.baseLocale] || url?.en

    Linking.openURL(store)
      .catch((error: unknown) => {
        logger.error('[AccountScreen.goToAppStore] Error opening link to store', { store }, error)
        if (localizedUrl) {
          return Linking.openURL(localizedUrl)
        }
      })
      .catch((anotherReason: unknown) => {
        logger.error(
          '[AccountScreen.goToAppStore] Error opening link to store',
          { localizedUrl },
          anotherReason
        )
      })
      .then(() => rateApp('clickedToRateAppFromProfile'))
  }
  if (isLoggingOut) {
    return (
      <NoticeBarContainer containerStyle={{ paddingBottom: theme.insets.bottom }}>
        <StyledActivityIndicatorContainer>
          <ActivityIndicator color={theme.colors.secondary} />
        </StyledActivityIndicatorContainer>
      </NoticeBarContainer>
    )
  }

  return (
    <NoticeBarContainer containerStyle={{ paddingBottom: theme.insets.bottom }}>
      <ResponsiveScrollView>
        <ProfileHeaderCard
          testID="ACCOUNT_SCREEN_PROFILE_HEADER"
          givenName={givenName}
          preferredName={preferredName}
          familyName={familyName}
          pronouns={pronouns}
          subTitle={email}
        />
        {Config.HAS_CUSTOM_ACCOUNT_BANNER && <VirtualCareAccountBanner />}
        <List>
          <ListItem
            key="coverage"
            analyticsName="coverage"
            testID="ACCOUNT_SCREEN_COVERAGE_MENU_ITEM"
            title={I18n.t('CoverageScreen.title')}
            onPress={() => navigation.navigate('coverage')}
          />
          <ListItem
            key="profile"
            analyticsName="profile"
            testID="ACCOUNT_SCREEN_ACCOUNT_MENU_ITEM"
            title={I18n.t('AccountScreen.profile')}
            onPress={() => navigation.navigate('profile')}
          />
          <PharmacyAccountMenuItem />
          {showFamily && hasEligibilityIntervals && (
            <ListItem
              key="family"
              analyticsName="family"
              testID="ACCOUNT_SCREEN_FAMILY_MENU_ITEM"
              title={I18n.t('AccountScreen.family')}
              onPress={() => navigation.navigate('family')}
            />
          )}
          {serviceFeatures.fitness_trackers && hasEligibilityIntervals && (
            <ListItem
              key="trackers"
              analyticsName="trackers"
              testID="ACCOUNT_SCREEN_TRACKERS_MENU_ITEM"
              title={I18n.t('AccountScreen.trackers')}
              onPress={() => navigation.navigate('healthTrackerSettings')}
            />
          )}
          {((serviceFeatures.payment && hasEligibilityIntervals) || !!creditCard) && (
            <ListItem
              key="payment"
              analyticsName="payment"
              testID="ACCOUNT_SCREEN_PAYMENT_MENU_ITEM"
              title={I18n.t('AccountScreen.payment')}
              onPress={() => navigation.navigate(creditCard ? 'viewPayment' : 'addPayment')}
            />
          )}
          {showNotifications && (
            <ListItem
              key="notifications"
              analyticsName="notifications"
              testID="ACCOUNT_SCREEN_NOTIFICATIONS_MENU_ITEM"
              title={I18n.t('AccountScreen.notifications')}
              onPress={() => navigation.navigate('notifications')}
            />
          )}
          {showSecurity && (
            <ListItem
              key="security"
              analyticsName="security"
              testID="ACCOUNT_SCREEN_SECURITY_MENU_ITEM"
              title={I18n.t('AccountScreen.security')}
              onPress={() => navigation.navigate('security')}
            />
          )}
          <ListItem
            key="legal"
            analyticsName="legal"
            testID="ACCOUNT_SCREEN_LEGAL_MENU_ITEM"
            title={I18n.t('AccountScreen.legal')}
            onPress={() => navigation.navigate('legal')}
          />
          <ListItem
            key="helpCentre"
            analyticsName="helpCentre"
            testID="ACCOUNT_SCREEN_HELP_MENU_ITEM"
            title={I18n.t('AccountScreen.helpCentre')}
            onPress={() => navigation.navigate('helpCentre', { screenName: route.name })}
            IconComponent={() => <NotificationIconBadge unreadCount={unreadCount} />}
          />
          {Config.ACCOUNT_SCREEN_LINKS && (
            <AccountScreenLinkMenuItems accountScreenLinks={Config.ACCOUNT_SCREEN_LINKS} />
          )}
          {isMobile() && hasEligibilityIntervals && !isEmbeddedEnvironment() && (
            <ListItem
              key="rating"
              analyticsName="rating"
              testID="ACCOUNT_SCREEN_RATING_MENU_ITEM"
              title={I18n.t('AccountScreen.rating')}
              onPress={goToAppStore}
              iconName="exit-to-app"
            />
          )}
          {showDebugToggle && (
            <ListItem
              key="debug"
              analyticsName="debug"
              testID="ACCOUNT_SCREEN_DEBUG_MENU_ITEM"
              title="Enable Debug Menu"
              onPress={toggleDebugFab}
            />
          )}
          {isWeb() && (
            <ListItem
              key="qr-code-app-store"
              analyticsName="AccountScreen.qrCodeAppStore"
              testID="ACCOUNT_SCREEN_QR_CODE_APP_STORE_MENU_ITEM"
              title={
                <QRCodeTextContainer>
                  <Icon variant="materialIcons" src="qr-code" size={16} color="accent" />
                  <Typography variant="subheaderBold">{I18n.t('QRCodeAppModal.title')}</Typography>
                </QRCodeTextContainer>
              }
              accessibilityLabel={I18n.t('QRCodeAppModal.title')}
              onPress={() => setQRCodeModalOpen(true)}
            />
          )}
        </List>

        <QRCodeAppModal isVisible={isQRCodeModalOpen} onClose={() => setQRCodeModalOpen(false)} />
      </ResponsiveScrollView>
      {!isEmbeddedEnvironment() && (
        <ButtonContainer>
          <Button
            variant="secondary"
            widthVariant="full"
            testID="SIGN_OUT_BTN"
            title={I18n.t('AccountScreen.signOut')}
            analyticsName="AccountScreen.signOut"
            onPress={() => logout('Logout from profile')}
            disabled={!isConnected}
          />
        </ButtonContainer>
      )}
    </NoticeBarContainer>
  )
}

export default AccountScreen
